import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Shows an early satisfaction prompt 7-14 days after the user's first product
 * purchase, and only once the account itself is at least 7 days old.
 * Only shows once — never again after submission or dismissal.
 */
export function useEarlySatisfaction() {
  const { user } = useAuth();
  const [showPopup, setShowPopup] = useState(false);

  const check = useCallback(async () => {
    if (!user?.id) return;

    // 1. Already responded? Skip.
    const { data: existing } = await supabase
      .from("early_satisfaction_responses")
      .select("id")
      .eq("user_id", user.id)
      .limit(1);
    if (existing && existing.length > 0) return;

    // 2. Check localStorage to avoid re-showing after dismiss
    const dismissKey = `early_sat_dismissed_${user.id}`;
    if (localStorage.getItem(dismissKey)) return;

    // 3. Account must be at least 7 days old
    const { data: appUser } = await supabase
      .from("app_users")
      .select("created_at")
      .eq("id", user.id)
      .maybeSingle();

    if (!appUser?.created_at) return;

    const now = new Date();
    const accountCreated = new Date(appUser.created_at);
    const daysSinceAccountCreated = (now.getTime() - accountCreated.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceAccountCreated < 7) return;

    // 4. Get first product purchase date
    const { data: products } = await supabase
      .from("user_products")
      .select("created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true })
      .limit(1);

    if (!products || products.length === 0) return;

    const firstPurchase = new Date(products[0].created_at);
    const daysSince = (now.getTime() - firstPurchase.getTime()) / (1000 * 60 * 60 * 24);

    // Show between 7 and 14 days after purchase
    if (daysSince >= 7 && daysSince <= 14) {
      setShowPopup(true);
    }
  }, [user?.id]);

  useEffect(() => {
    check();
  }, [check]);

  const dismiss = useCallback(() => {
    if (user?.id) {
      localStorage.setItem(`early_sat_dismissed_${user.id}`, "1");
    }
    setShowPopup(false);
  }, [user?.id]);

  return { showPopup, dismiss, setShowPopup };
}
